/**
 * 外車批次頁（openspec add-external-driver-grab）— 群組連結的落點 /ext/batch/{token}
 * 卡片（比照船邊櫃出口卡）→ 狀態列（剩餘／已接／已完成，或已額滿）→ 我在此批的單（序號、完成）
 * → 我所有未完成的單 → 接單。樣式複製自 TaskBrowsePage 的出口卡，不共用元件（那頁綁會員狀態）。
 */
import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, Paper, TextField, Typography,
} from '@mui/material'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner'
import RefreshIcon from '@mui/icons-material/Refresh'
import PersonIcon from '@mui/icons-material/Person'
import HistoryIcon from '@mui/icons-material/History'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import { useLiff } from '@/contexts/LiffContext'
import BarcodeScanner from '@/components/BarcodeScanner'
import { ExtShell, ExtCenter, EXT_MAIN } from './ExtGuard'
import { cancelExtMission, completeExtMission, getExtBatch, grabExtBatch } from './extService'
import {
  ERR_LINE_AUTH_FAILED, ERR_LINK_INVALID, ERR_PLATFORM_DRIVER, ExtApiError, ExtNoIdTokenError, ExtNotBoundError,
  ensureExtSession, extErrorMessage,
} from './extApiClient'
import { EMPTY_LOADED_LABEL, MISSION_STATUS_LABEL, type ExtBatchResponse, type ExtMission } from './types'

const EXPORT_COLOR = '#ed6c02'

/** 剩餘／額滿狀態 Chip：2026-10 客戶要求字放大，尤其「剩幾櫃」 */
const BIG_CHIP = (bg: string) => ({
  backgroundColor: bg, color: 'white', fontWeight: 800, fontSize: '1.4rem', height: 48, px: 1,
  '& .MuiChip-label': { px: 1.5 },
})

const ExtBatchPage: React.FC = () => {
  const { token = '' } = useParams<{ token: string }>()
  const navigate = useNavigate()
  const { login } = useLiff()
  const backPath = `/ext/batch/${token}`

  const [data, setData] = useState<ExtBatchResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [fatal, setFatal] = useState<{ title: string; message: string; home?: boolean } | null>(null)
  const [toast, setToast] = useState<{ severity: 'success' | 'error' | 'info'; text: string } | null>(null)
  const [grabbing, setGrabbing] = useState(false)
  const [grabConfirm, setGrabConfirm] = useState(false)
  // 雙拖詢問（2026-09-29 比照會員 TaskBrowsePage 的 doubleTowAsk）：20 呎且剩餘 >= 2、手上沒有未完成時先問接幾櫃
  const [doubleTowAsk, setDoubleTowAsk] = useState(false)
  const [completeTarget, setCompleteTarget] = useState<ExtMission | null>(null)
  const [containerNo, setContainerNo] = useState('')
  const [completing, setCompleting] = useState(false)
  const [scannerOpen, setScannerOpen] = useState(false)
  const [cancelTarget, setCancelTarget] = useState<ExtMission | null>(null)
  const [cancelling, setCancelling] = useState(false)

  const handleAuthError = useCallback(async (err: unknown): Promise<boolean> => {
    if (err instanceof ExtNotBoundError) {
      navigate(`/ext/bind?back=${encodeURIComponent(backPath)}`, { replace: true })
      return true
    }
    if (err instanceof ExtNoIdTokenError) {
      await login(true)
      return true
    }
    if (err instanceof ExtApiError) {
      if (err.code === ERR_PLATFORM_DRIVER) {
        setFatal({ title: '您是平台司機', message: '這個連結是給同業司機用的，請回到好運網任務頁接單。', home: true })
        return true
      }
      if (err.code === ERR_LINE_AUTH_FAILED) {
        await login(true)
        return true
      }
      if (err.code === ERR_LINK_INVALID) {
        setFatal({ title: '連結已失效', message: '這批放櫃已結束或連結已過期，請向調派人員確認。' })
        return true
      }
    }
    return false
  }, [backPath, login, navigate])

  const load = useCallback(async () => {
    setLoading(true)
    try {
      await ensureExtSession()
      setData(await getExtBatch(token))
      setFatal(null)
    } catch (err) {
      if (!(await handleAuthError(err))) setToast({ severity: 'error', text: extErrorMessage(err) })
    } finally {
      setLoading(false)
    }
  }, [token, handleAuthError])

  useEffect(() => { load() }, [load])

  /**
   * 接單。wanted=2 = 雙拖：連續呼叫兩次接單 API（照抄會員 TaskBrowsePage.handleGrabShipsideRow）。
   * 第二櫃可能被別人搶走，此時不回退第一櫃（已在執行中，退回等於替司機做決定），如實告知只接到 1 櫃。
   */
  const handleGrab = async (wanted: 1 | 2 = 1) => {
    setGrabConfirm(false)
    setDoubleTowAsk(false)
    setGrabbing(true)
    try {
      const r = await grabExtBatch(token)
      let text = r.mission?.seq ? `接單成功，您是第 ${r.mission.seq} 位` : '接單成功'
      let severity: 'success' | 'info' = 'success'
      if (wanted === 2) {
        try {
          await grabExtBatch(token)
          text = '接單成功！已接 2 櫃（雙拖）'
        } catch (err2) {
          if (await handleAuthError(err2)) return
          text = `只接到 1 櫃（第 2 櫃：${extErrorMessage(err2)}）。已接的 1 櫃仍為執行中。`
          severity = 'info'
        }
      }
      setToast({ severity, text })
      await load()
    } catch (err) {
      if (!(await handleAuthError(err))) {
        setToast({ severity: 'error', text: extErrorMessage(err) })
        await load()
      }
    } finally {
      setGrabbing(false)
    }
  }

  const handleComplete = async () => {
    if (!completeTarget) return
    setCompleting(true)
    try {
      await completeExtMission(completeTarget.missionId, containerNo)
      setCompleteTarget(null)
      setContainerNo('')
      setToast({ severity: 'success', text: '任務已完成' })
      await load()
    } catch (err) {
      if (!(await handleAuthError(err))) setToast({ severity: 'error', text: extErrorMessage(err) })
    } finally {
      setCompleting(false)
    }
  }

  /** 取消接單（比照會員 TaskBrowsePage 的取消任務：僅執行中可取消，櫃回搶單池） */
  const handleCancel = async (alsoCancelPaired: boolean) => {
    if (!cancelTarget) return
    setCancelling(true)
    try {
      const partner = alsoCancelPaired ? cancelPartnerOf(cancelTarget) : null
      await cancelExtMission(cancelTarget.missionId, partner?.missionId ?? null)
      setCancelTarget(null)
      setToast({ severity: 'success', text: partner ? '已取消 2 櫃（雙拖）' : '已取消接單' })
      await load()
    } catch (err) {
      if (!(await handleAuthError(err))) setToast({ severity: 'error', text: extErrorMessage(err) })
    } finally {
      setCancelling(false)
    }
  }

  /**
   * 雙拖配對推斷（照抄會員 TaskBrowsePage「推斷配對」）：外車接單不寫 paired_mission_id，
   * 手上正好 2 筆執行中、都是 20 呎、同起訖、同船掛 → 視為同一趟雙拖。後端並行規則保證只有這種情況能同時有 2 筆。
   */
  const cancelPartnerOf = (m: ExtMission): ExtMission | null => {
    const running = (data?.unfinished ?? []).filter((u) => u.status === '20')
    if (running.length !== 2 || !running.some((u) => u.missionId === m.missionId)) return null
    const [a, b] = running
    const same = String(a.containerSize) === '20' && String(b.containerSize) === '20'
      && a.originLocation === b.originLocation && a.destinationLocation === b.destinationLocation
      && a.vesselCode === b.vesselCode
    if (!same) return null
    return a.missionId === m.missionId ? b : a
  }

  if (fatal) {
    return (
      <ExtCenter>
        <Paper elevation={3} sx={{ p: 3, borderRadius: 3, textAlign: 'center', width: '100%', maxWidth: 400 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>{fatal.title}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{fatal.message}</Typography>
          {fatal.home && <Button variant="contained" onClick={() => navigate('/', { replace: true })} sx={{ backgroundColor: EXT_MAIN }}>回到任務頁</Button>}
        </Paper>
      </ExtCenter>
    )
  }

  if (loading && !data) {
    return (
      <ExtCenter>
        <CircularProgress size={56} sx={{ color: EXT_MAIN }} />
        <Typography variant="body1" color="text.secondary">讀取放櫃資料…</Typography>
      </ExtCenter>
    )
  }

  const card = data?.card
  const mine = data?.mine ?? []
  const unfinished = data?.unfinished ?? []
  const full = data?.status === 'FULL'
  // 整批已全數完成：照常顯示卡片與「我在這批接的單」，只是不能再接（2026-09-29 之前會變成「連結已失效」）
  const closed = data?.status === 'CLOSED'
  // 比照會員：手上有 1 筆 20 呎時，這批也是 20 呎且同起訖可再接一櫃（雙拖）
  const blockText = data?.blockReason === 'HAS_UNFINISHED' ? '您尚有未完成的任務，完成後才能再接（20 呎同起訖可再接一櫃雙拖）' : null

  return (
    <ExtShell>
      {/* 標題列 */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: EXT_MAIN }}>同業接單</Typography>
        <Box>
          <IconButton size="small" title="接單紀錄" onClick={() => navigate(`/ext/history?back=${encodeURIComponent(backPath)}`)} sx={{ color: EXT_MAIN }}><HistoryIcon /></IconButton>
          <IconButton size="small" title="我的資料" onClick={() => navigate(`/ext/me?back=${encodeURIComponent(backPath)}`)} sx={{ color: EXT_MAIN }}><PersonIcon /></IconButton>
          <IconButton size="small" onClick={load} disabled={loading} sx={{ color: EXT_MAIN }}><RefreshIcon /></IconButton>
        </Box>
      </Box>

      {toast && <Alert severity={toast.severity} sx={{ mb: 1.5 }} onClose={() => setToast(null)}>{toast.text}</Alert>}

      {/* 卡片（比照出口船邊櫃卡） */}
      {card && (
        <Paper elevation={2} sx={{
          borderRadius: 2, border: `2px solid ${EXPORT_COLOR}`, overflow: 'hidden', backgroundColor: 'white',
          boxShadow: `inset 0 0 0 1px ${EXPORT_COLOR}55, inset 0 0 12px ${EXPORT_COLOR}33`, mb: 2,
        }}>
          <Box sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, minWidth: 0 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: EXT_MAIN, fontSize: '1.35rem' }}>{card.vesselCode}</Typography>
                {card.vesselName && <Typography sx={{ fontWeight: 600, color: EXT_MAIN, fontSize: '1.1rem' }} noWrap>{card.vesselName}</Typography>}
              </Box>
              <Chip label="出口" size="small" sx={{ backgroundColor: EXPORT_COLOR, color: 'white', fontWeight: 600, fontSize: '0.7rem', height: 22 }} />
            </Box>
            {card.releaseRemark && (
              <Typography sx={{ color: '#d32f2f', fontWeight: 700, mb: 1, fontSize: '1.1rem' }}>備註：{card.releaseRemark}</Typography>
            )}
            <Box sx={{ p: 1.25, backgroundColor: '#f5f5f5', borderRadius: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5, flexWrap: 'wrap' }}>
                <Typography sx={{ fontWeight: 700, fontSize: '1.15rem' }}>{card.originShortName || card.originLocation || '-'}</Typography>
                <ArrowForwardIcon sx={{ fontSize: 22, mx: 0.5, color: EXT_MAIN }} />
                <Typography sx={{ fontWeight: 700, fontSize: '1.15rem' }}>卸貨港 {card.dischargePort || '-'}</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Chip label={card.containerSize ? `${card.containerSize} 呎` : '-'}
                  sx={{ fontWeight: 700, fontSize: '1rem', height: 30, backgroundColor: '#2196f3', color: 'white' }} />
                <Typography sx={{ color: EXT_MAIN, fontWeight: 600, fontSize: '1.1rem' }}>{EMPTY_LOADED_LABEL[card.emptyLoaded ?? ''] ?? card.emptyLoaded ?? ''}</Typography>
                <Typography sx={{ fontWeight: 700, color: '#ff9800', fontSize: '1.15rem' }}>放櫃 {card.total} 櫃</Typography>
              </Box>
            </Box>
            {/* 狀態列：他人只給統計數字 */}
            <Box sx={{ display: 'flex', gap: 1, mt: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
              {closed ? (
                <Chip label="本批已全數完成" sx={BIG_CHIP('#9e9e9e')} />
              ) : full ? (
                <Chip label="已額滿" sx={BIG_CHIP('#9e9e9e')} />
              ) : (
                <Chip label={`剩餘 ${card.remaining} 櫃`} sx={BIG_CHIP('#4caf50')} />
              )}
              <Typography color="text.secondary" sx={{ fontSize: '1.05rem', fontWeight: 600 }}>已接 {card.accepted} · 已完成 {card.completed}</Typography>
            </Box>
          </Box>
        </Paper>
      )}

      {/* 接單：額滿時整顆按鈕變紅、寫「已額滿」、不可按（使用者 2026-09-18）；其他擋單原因仍顯示在按鈕下方 */}
      {closed ? (
        <Button fullWidth variant="contained" size="large" disabled
          sx={{ fontWeight: 700, py: 1.75, mb: 2, fontSize: '1.3rem',
            '&.Mui-disabled': { backgroundColor: '#9e9e9e', color: 'white' } }}>
          本批已結束
        </Button>
      ) : full ? (
        <Button fullWidth variant="contained" size="large" disabled
          sx={{ backgroundColor: '#d32f2f', color: 'white', fontWeight: 700, py: 1.75, mb: 2, fontSize: '1.3rem',
            '&.Mui-disabled': { backgroundColor: '#d32f2f', color: 'white', opacity: 0.9 } }}>
          已額滿
        </Button>
      ) : (
        <>
          <Button fullWidth variant="contained" size="large" disabled={!data?.canGrab || grabbing || loading}
            onClick={() => {
              // 比照會員：20 呎且剩餘 2 櫃以上 → 先問要不要雙拖。
              // 另加「手上沒有未完成」：已有 1 櫃時後端只放行再接 1 櫃，問了也接不到第 2 櫃。
              if (String(card?.containerSize) === '20' && (card?.remaining ?? 0) >= 2 && unfinished.length === 0) {
                setDoubleTowAsk(true)
              } else {
                setGrabConfirm(true)
              }
            }}
            sx={{ backgroundColor: '#4caf50', color: 'white', fontWeight: 700, py: 1.75, mb: 1, fontSize: '1.3rem',
              '&:hover': { backgroundColor: '#388e3c' }, '&:disabled': { backgroundColor: '#a5d6a7', color: 'white' } }}>
            {grabbing ? <CircularProgress size={22} sx={{ color: 'white' }} /> : '我要接單'}
          </Button>
          {blockText && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mb: 2 }}>{blockText}</Typography>}
        </>
      )}

      {/* 我在此批的單 */}
      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: EXT_MAIN, mt: 2, mb: 1 }}>我在這批接的單</Typography>
      {mine.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>尚未接單</Typography>
      ) : mine.map((m) => <MissionRow key={m.missionId} m={m} showSeq onComplete={() => { setCompleteTarget(m); setContainerNo(m.containerNo ?? '') }} onCancel={() => setCancelTarget(m)} />)}

      {/* 我所有未完成（跨批） */}
      {unfinished.some((u) => u.batchKey !== card?.batchKey) && (
        <>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: EXT_MAIN, mt: 2, mb: 1 }}>其他批次未完成的單</Typography>
          {unfinished.filter((u) => u.batchKey !== card?.batchKey).map((m) => (
            <MissionRow key={m.missionId} m={m} onComplete={() => { setCompleteTarget(m); setContainerNo(m.containerNo ?? '') }} onCancel={() => setCancelTarget(m)} />
          ))}
        </>
      )}

      {/* 接單確認 */}
      <Dialog open={grabConfirm} onClose={() => setGrabConfirm(false)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 700 }}>確認接單</DialogTitle>
        <DialogContent>
          <Typography variant="body2">接單後即視為執行中，完成後請記得按「完成任務」。</Typography>
          {card && (
            <Typography variant="body2" sx={{ mt: 1.5, fontWeight: 600 }}>
              {card.vesselCode} {card.originShortName || card.originLocation} → {card.dischargePort} · {card.containerSize} 呎 {EMPTY_LOADED_LABEL[card.emptyLoaded ?? ''] ?? ''}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={() => setGrabConfirm(false)}>取消</Button>
          <Button variant="contained" onClick={() => handleGrab(1)} sx={{ backgroundColor: '#4caf50', fontWeight: 700 }}>確認接單</Button>
        </DialogActions>
      </Dialog>

      {/* 雙拖詢問（文案與按鈕照抄會員 TaskBrowsePage 的「要接雙拖嗎？」） */}
      <Dialog open={doubleTowAsk} onClose={() => setDoubleTowAsk(false)} fullWidth maxWidth="xs" PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, color: EXT_MAIN, textAlign: 'center', pt: 3 }}>要接雙拖嗎？</DialogTitle>
        <DialogContent>
          {card && (
            <Box sx={{ textAlign: 'center' }}>
              <Box sx={{ p: 1.5, backgroundColor: '#f5f5f5', borderRadius: 2, mb: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: EXT_MAIN, mb: 0.5 }}>
                  {card.vesselCode}{card.vesselName && ` - ${card.vesselName}`}
                </Typography>
                <Typography variant="body2" sx={{ color: '#333' }}>
                  {card.originShortName || card.originLocation || '-'} → 卸貨港 {card.dischargePort || '-'}
                </Typography>
                <Typography variant="body2" sx={{ color: '#ff9800', fontWeight: 700, mt: 0.5 }}>
                  20 呎 {EMPTY_LOADED_LABEL[card.emptyLoaded ?? ''] ?? ''}，剩餘 {card.remaining} 櫃
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                這批同起訖的 20 呎櫃還有 {card.remaining} 櫃，可以一趟拖兩櫃。
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                若第 2 櫃剛好被別人搶走，會只接到 1 櫃（已接的那櫃仍有效）。
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2.5, flexDirection: 'column', gap: 1 }}>
          <Button fullWidth variant="contained" onClick={() => handleGrab(2)}
            sx={{ py: 1.2, fontSize: '1rem', fontWeight: 700, backgroundColor: '#4caf50', '&:hover': { backgroundColor: '#388e3c' } }}>
            接 2 櫃（雙拖）
          </Button>
          <Button fullWidth variant="outlined" onClick={() => handleGrab(1)}
            sx={{ py: 1.2, fontSize: '1rem', fontWeight: 600, color: EXT_MAIN, borderColor: EXT_MAIN }}>
            只接 1 櫃
          </Button>
          <Button fullWidth onClick={() => setDoubleTowAsk(false)} sx={{ color: '#888' }}>取消</Button>
        </DialogActions>
      </Dialog>

      {/* 完成任務：櫃號掃描或手打（起訖任一為自動掃描地點時可免填，由後端判定） */}
      <Dialog open={!!completeTarget} onClose={() => !completing && setCompleteTarget(null)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 700 }}>完成任務</DialogTitle>
        <DialogContent>
          {completeTarget && (
            <Typography variant="body2" sx={{ mb: 2 }}>
              {completeTarget.vesselCode} · {completeTarget.originShortName || completeTarget.originLocation} → {completeTarget.destShortName || completeTarget.destinationLocation}
            </Typography>
          )}
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
            <TextField fullWidth label="貨櫃號碼" placeholder="例：TCLU1234567" value={containerNo}
              onChange={(e) => setContainerNo(e.target.value.toUpperCase())} inputProps={{ maxLength: 11, autoCapitalize: 'characters' }}
              helperText="起訖地皆非自動掃描時必填" />
            <IconButton onClick={() => setScannerOpen(true)} sx={{ color: '#2196f3', mt: 1 }}><QrCodeScannerIcon /></IconButton>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={() => setCompleteTarget(null)} disabled={completing}>取消</Button>
          <Button variant="contained" onClick={handleComplete} disabled={completing} startIcon={<CheckCircleIcon />}
            sx={{ backgroundColor: EXT_MAIN, fontWeight: 700 }}>
            {completing ? <CircularProgress size={20} sx={{ color: 'white' }} /> : '完成任務'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 取消接單確認（文案比照會員 TaskBrowsePage） */}
      <Dialog open={!!cancelTarget} onClose={() => !cancelling && setCancelTarget(null)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 700 }}>取消接單</DialogTitle>
        <DialogContent>
          {cancelTarget && (
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
              {cancelTarget.vesselCode} · {cancelTarget.originShortName || cancelTarget.originLocation} → {cancelTarget.destShortName || cancelTarget.destinationLocation}
            </Typography>
          )}
          {cancelTarget && cancelPartnerOf(cancelTarget) ? (
            <Typography variant="body2" color="text.secondary">
              偵測到雙拖配對（另一櫃同 20 呎、同起訖地，由您執行中）。
              取消後櫃會回搶單池，其他司機可重新接單。
              <br />
              <Box component="span" sx={{ fontWeight: 600, color: '#d32f2f' }}>是否一併取消第二櫃？</Box>
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary">取消後櫃會回搶單池，其他司機可重新接單。</Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <Button onClick={() => setCancelTarget(null)} disabled={cancelling} sx={{ color: '#666' }}>不取消</Button>
          {cancelTarget && cancelPartnerOf(cancelTarget) ? (
            <>
              <Button variant="outlined" onClick={() => handleCancel(false)} disabled={cancelling}
                sx={{ borderColor: '#d32f2f', color: '#d32f2f' }}>只取消這筆</Button>
              <Button variant="contained" onClick={() => handleCancel(true)} disabled={cancelling}
                sx={{ backgroundColor: '#d32f2f', '&:hover': { backgroundColor: '#b71c1c' }, fontWeight: 700 }}>
                {cancelling ? <CircularProgress size={20} sx={{ color: 'white' }} /> : '兩筆都取消'}
              </Button>
            </>
          ) : (
            <Button variant="contained" color="error" onClick={() => handleCancel(false)} disabled={cancelling} sx={{ fontWeight: 700 }}>
              {cancelling ? <CircularProgress size={20} sx={{ color: 'white' }} /> : '確認取消'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <BarcodeScanner open={scannerOpen} onClose={() => setScannerOpen(false)} title="掃描貨櫃號碼"
        onSuccess={(decoded) => { setContainerNo(decoded); setScannerOpen(false) }} />
    </ExtShell>
  )
}

const MissionRow: React.FC<{ m: ExtMission; showSeq?: boolean; onComplete: () => void; onCancel: () => void }> = ({ m, showSeq, onComplete, onCancel }) => {
  const done = m.status === '30'
  const inProgress = m.status === '20'
  return (
    <Paper elevation={1} sx={{ p: 1.5, mb: 1, borderRadius: 2, display: 'flex', alignItems: 'center', gap: 1, opacity: done ? 0.7 : 1 }}>
      {showSeq && (
        <Box sx={{ minWidth: 40, height: 40, borderRadius: '50%', backgroundColor: done ? '#9e9e9e' : EXT_MAIN, color: 'white',
          display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700 }}>
          {m.seq ?? '-'}
        </Box>
      )}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
          {m.vesselCode} · {m.originShortName || m.originLocation || '-'} → {m.destShortName || m.destinationLocation || '-'}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {m.containerSize ? `${m.containerSize} 呎` : ''} {EMPTY_LOADED_LABEL[m.emptyLoaded ?? ''] ?? ''} · {MISSION_STATUS_LABEL[m.status] ?? m.status}
          {m.containerNo ? ` · ${m.containerNo}` : ''}
        </Typography>
      </Box>
      {!done && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, flexShrink: 0 }}>
          <Button size="small" variant="outlined" onClick={onComplete} sx={{ borderColor: EXT_MAIN, color: EXT_MAIN, fontWeight: 600 }}>
            完成
          </Button>
          {/* 比照會員：僅執行中可取消 */}
          {inProgress && (
            <Button size="small" variant="text" color="error" onClick={onCancel} sx={{ fontWeight: 600, minWidth: 0 }}>
              取消
            </Button>
          )}
        </Box>
      )}
    </Paper>
  )
}

export default ExtBatchPage
